import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/api";
import axiosPrivate from "../../services/axios/axiosPrivate";

/* FETCH ALL ================= */
export const fetchAnnouncements = createAsyncThunk(
  "announcements/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(api.announcements.getAll);
      return res.data.data.content;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Fetch failed"
      );
    }
  }
);

/* FETCH BY ID ================= */
export const fetchAnnouncementById = createAsyncThunk(
  "announcements/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        `${api.announcements.getById}${id}`
      );
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Fetch failed"
      );
    }
  },
  {
    condition: (id, { getState }) => {
      const { announcements } = getState();

      if (announcements.detailsLoading) {
        return false;
      }

      return true;
    }
  }
);

/* CREATE ================= */
export const createAnnouncement = createAsyncThunk(
  "announcements/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(
        api.announcements.getAll,
        payload
      );
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Create failed"
      );
    }
  }
);

/* UPDATE ================= */
export const updateAnnouncement = createAsyncThunk(
  "announcements/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.put(
        `${api.announcements.getById}${id}`,
        payload
      );
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Update failed"
      );
    }
  }
);

/* GET PUBLISHED ================= */
export const getPublishedAnnouncements = createAsyncThunk(
  "announcements/getPublished",
  async ({ page, size }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        `${api.announcements.getPublished}?page=${page}&size=${size}`);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "get published ann. failed"
      );
    }
  }
);

/* ANN. ACTION ================= */
export const createAnnouncementAction = createAsyncThunk(
  "announcements/createAnnouncementAction",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(
        `${api.announcements.getById}${id}/like`);
      console.log("resp:", res.data.data);
      return res.data.data;
    } catch (error) {
      console.log("err:", error?.response?.data?.message);
      return rejectWithValue(
        error?.response?.data?.message || "posting ann. action failed"
      );
    }
  }
);

/* DELETE ================= */
export const deleteAnnouncement = createAsyncThunk(
  "announcements/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosPrivate.delete(`${api.announcements.getById}${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Delete failed"
      );
    }
  }
);

/* Comments list ================= */
export const fetchAnnouncementComments = createAsyncThunk(
  "announcements/fetchComments",
  async ({ id, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.get(
        `${api.announcements.getComments(id)}?page=${page}&size=${size}`
      );

      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Fetch comments failed"
      );
    }
  }
);

/* ADD COMMENT ================= */
export const addAnnouncementComment = createAsyncThunk(
  "announcements/addComment",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await axiosPrivate.post(
        `${api.announcements.getById}${id}/comment`,
        { comment }
      );

      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Add comment failed"
      );
    }
  }
);

const announcementsSlice = createSlice({
  name: "announcements",
  initialState: {
    list: [],
    selected: null,
    listLoading: false,
    detailsLoading: false,
    currentRequestId: undefined,
    publishedStatus: "idle",
    publishedList: [],
    publishedErr: null,
    error: null,
    commentsData: null,
    commentsLoading: false,
    actionLoading: false,
    actionErr: null,
  },
  reducers: {
    clearSelectedAnnouncement: (state) => {
      state.selected = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* FETCH ALL */
      .addCase(fetchAnnouncements.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* FETCH PUBLISHED */
      .addCase(getPublishedAnnouncements.pending, (state) => {
        if (state.publishedList.length === 0) {
          state.publishedStatus = "loading";
          state.loading = true;
          state.publishedErr = null;
        }
      })
      .addCase(getPublishedAnnouncements.fulfilled, (state, action) => {
        state.publishedStatus = 'succeeded';
        state.loading = false;
        state.publishedList = action.payload?.content || [];
      })
      .addCase(getPublishedAnnouncements.rejected, (state, action) => {
        state.publishedStatus = 'failed';
        state.loading = false;
        state.publishedErr = action.payload;
      })

      /* FETCH BY ID */
      .addCase(fetchAnnouncementById.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncementById.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selected = action.payload;
      })
      .addCase(fetchAnnouncementById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload;
      })

      /* CREATE */
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
      })

      /* UPDATE */
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        const index = state.list.findIndex(
          (a) => a.id === action.payload.id
        );
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })

      /* CREATE ANN. ACTION */
      .addCase(createAnnouncementAction.pending, (state) => {
        state.actionLoading = true;
        state.actionErr = null;
      })
      .addCase(createAnnouncementAction.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createAnnouncementAction.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionErr = action.payload;
      })

      /* DELETE */
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (a) => a.id !== action.payload
        );
      })
      /* Comments */
      .addCase(fetchAnnouncementComments.pending, (state) => {
        state.commentsLoading = true;
      })
      .addCase(fetchAnnouncementComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.commentsData = action.payload;  // ✅ direct payload
      })
      .addCase(fetchAnnouncementComments.rejected, (state) => {
        state.commentsLoading = false;
      })
      /* ADD COMMENT */
      .addCase(addAnnouncementComment.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addAnnouncementComment.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addAnnouncementComment.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionErr = action.payload;
      });

  },
});

export const { clearSelectedAnnouncement } =
  announcementsSlice.actions;

export default announcementsSlice.reducer;
